import { useEffect, useRef, useCallback } from "react";

/**
 * Returns a ref callback for <video> elements.
 * Sets src and plays only when the video enters the viewport.
 * Pauses when it leaves.
 */
export function useLazyVideo() {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const videosRef = useRef<Set<HTMLVideoElement>>(new Set());

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target as HTMLVideoElement;
          const dataSrc = video.dataset.src;
          if (entry.isIntersecting) {
            if (dataSrc && !video.src) {
              video.src = dataSrc;
            }
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { rootMargin: "200px" }
    );

    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  const ref = useCallback((el: HTMLVideoElement | null) => {
    if (el && observerRef.current && !videosRef.current.has(el)) {
      videosRef.current.add(el);
      observerRef.current.observe(el);
    }
  }, []);

  return ref;
}
